import React, { Fragment, useEffect, useRef, useContext } from 'react';
import _merge from 'lodash/merge';
import ArrowTriangleDownIcon from '@nokia-csf-uxr/ccfk-assets/ArrowTriangleDownIcon';
import { getContext, useCombinedRefs } from 'common';
import useDidUpdateEffect from 'common/useDidUpdateEffect';
import { SelectItemBasicContext } from './SelectItemBasic';
import Styled from './styles';

export interface SelectItemButtonProps extends Omit<React.ComponentPropsWithRef<'div'>, 'placeholder'> {
  /** The element to use as the dropdown icon (usually a down arrow icon). */
  dropdownIcon?: React.ReactNode;
  /** The item selected from the dropdown List. It will appear in the SelectItem base area.  */
  children?: React.ReactNode;
  /** The short hint displayed in the SelectItem base area when no item has been selected. */
  placeholder?: string | React.ReactNode;
  /** DOM attributes applied to the hidden input element. Field validation is controlled by you. Note: DOM attributes should be camelCased. */
  inputProps?: Partial<React.ComponentPropsWithRef<'input'>>;
  /** Renders a Button onto the SelectItem; can be used to clear the input value. Consider using SelectItemClearButton. */
  renderClearButton?(...args: unknown[]): React.ReactNode;
  /** @ignore */
  onClick?: React.MouseEventHandler;
  /** @ignore */
  onKeyDown?: React.KeyboardEventHandler;
  /** @ignore */
  onMouseOver?: React.MouseEventHandler;
  /** @ignore */
  onMouseLeave?: React.MouseEventHandler;
  /** @ignore */
  onFocus?: React.FocusEventHandler;
  /** @ignore */
  onBlur?: React.FocusEventHandler;
}

/** SelectItemButton is a component to render the item(s) selected from the dropdown List and the dropdown indicator Icon into the
 * SelectItem base area. All other props will be provided to wrapper element. */
const SelectItemButton = React.forwardRef<HTMLElement, SelectItemButtonProps>((props: SelectItemButtonProps, ref) => getContext(({ RTL, theme }) => {
  const {
    dropdownIcon,
    children,
    placeholder,
    inputProps,
    renderClearButton,
    ...otherProps
  } = props;
  const buttonRef = useRef(null);
  const combinedRef = useCombinedRefs(ref, buttonRef);
  const selectItemBasicContext = useContext(SelectItemBasicContext);

  const focusButton = () => {
    buttonRef && buttonRef.current && buttonRef.current.focus();
  };

  useEffect(() => {
    if (selectItemBasicContext.autoFocus) {
      focusButton();
    }
  }, []);

  // only want this effect after initial render
  useDidUpdateEffect(!selectItemBasicContext.disabled && !selectItemBasicContext.isOpen && !selectItemBasicContext.clickedOutside ? focusButton : undefined, [selectItemBasicContext.isOpen]);

  const handleFocus = (event) => {
    const { onFocus } = props;
    onFocus && onFocus(event);
  };

  const handleKeyDown = (event) => {
    const { onKeyDown } = props;
    onKeyDown && onKeyDown(event);
  };

  const globalTextInputTheme = selectItemBasicContext.variant === 'outlined' ? theme.TEXT_INPUT.OUTLINED : theme.TEXT_INPUT.UNDERLINED;
  const resolvedTheme = _merge({}, globalTextInputTheme, selectItemBasicContext.resolvedTextInputTheme);
  const hideClearButton =
    selectItemBasicContext.disabled || renderClearButton === undefined;
  return (
    <Fragment>
      <Styled.SelectItemButton
        tabIndex={0}
        role="button"
        ref={combinedRef}
        RTL={RTL}
        theme={resolvedTheme}
        disabled={selectItemBasicContext.disabled}
        $multiSelect={selectItemBasicContext.multiSelect}
        {...otherProps}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
      >
        {children || <Styled.Placeholder theme={resolvedTheme}>{placeholder}</Styled.Placeholder>}
      </Styled.SelectItemButton>
      <Styled.SelectIndicators RTL={RTL}>
        <Styled.DropDownIndicator
          $isOpen={selectItemBasicContext.isOpen && !selectItemBasicContext.disabled}
          theme={resolvedTheme}
          disabled={selectItemBasicContext.disabled}
          hide={!hideClearButton}
        >
          {dropdownIcon}
        </Styled.DropDownIndicator>
        <Styled.ClearButton RTL={RTL} disabled={selectItemBasicContext.disabled} hide={hideClearButton}>
          {renderClearButton && renderClearButton()}
        </Styled.ClearButton>
      </Styled.SelectIndicators>
      <Styled.HiddenInput aria-hidden tabIndex={-1} readOnly disabled={selectItemBasicContext.disabled} {...inputProps} type="hidden" />
    </Fragment>
  );
}));

SelectItemButton.displayName = 'SelectItemButton';

SelectItemButton.defaultProps = {
  dropdownIcon: <ArrowTriangleDownIcon />,
  children: undefined,
  placeholder: undefined,
  inputProps: {},
  renderClearButton: undefined,
  onClick: undefined,
  onKeyDown: undefined,
  onMouseOver: undefined,
  onMouseLeave: undefined,
  onFocus: undefined,
  onBlur: undefined
};

export {
  SelectItemButton as default,
};
